// React
import React, { useRef, useState, useEffect } from "react";

// Custom
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";
import { useHorizontalGalleryStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import Lightbox from "Custom/Components/Popups/Lightbox";

type GalleryProps = {
    images: Array<any>;
    onImageSelect?: (selectedImage: any) => void;
    className?: string;
};

export const HoverScrollableGallery: React.FC<GalleryProps> = ({ images, onImageSelect, className }) => {
    const galleryStyles = useHorizontalGalleryStyles();

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollInterval = useRef<number | null>(null);
    const [showArrows, setShowArrows] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [lightboxImg, setLightboxImg] = useState("");
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxCaption, setLightboxCaption] = useState("");

    useEffect(() => {
        const checkOverflow = () => {
            if (scrollContainerRef.current) {
                const { scrollWidth, clientWidth } = scrollContainerRef.current;
                setShowArrows(scrollWidth > clientWidth);
            }
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);

        return () => {
            window.removeEventListener("resize", checkOverflow);
        };
    }, [images]);

    useEffect(() => {
        const firstVisibleIndex = images.findIndex(image => !!image && !!image.imageUrl);
        setSelectedIndex(firstVisibleIndex >= 0 ? firstVisibleIndex : 0);
    }, [images]);

    const startScrolling = (direction: "left" | "right") => {
        const scrollAmount = direction === "left" ? -5 : 5;
        scrollInterval.current = window.setInterval(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollBy({ left: scrollAmount });
            }
        }, 10);
    };

    const stopScrolling = () => {
        if (scrollInterval.current !== null) {
            clearInterval(scrollInterval.current);
            scrollInterval.current = null;
        }
    };

    const handleImageClick = (image: any, index: number) => {
        if (onImageSelect) {
            onImageSelect(image);
        }

        setLightboxImg(image.imageUrl.split(".").pop() !== "pdf" ? image.imageUrl : image.thumbnailImageUrl);
        setLightboxCaption(image.caption);
        setLightboxOpen(true);
    };

    return (
        <>
            <div
                style={{
                    position: "relative",
                    margin: "0 auto",
                    overflow: "hidden",
                    borderRadius: "8px",
                }}
            >
                <div
                    ref={scrollContainerRef}
                    style={{
                        display: "flex",
                        overflowX: "scroll",
                        gap: "10px",
                        padding: "10px 0",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                    }}
                    className={galleryStyles.hideScrollbar}
                >
                    {images.map(
                        (image, index) =>
                            image &&
                            image.imageUrl && (
                                <PrivateBlob
                                    key={index}
                                    src={image.imageUrl.split(".").pop() !== "pdf" ? image.imageUrl : image.thumbnailImageUrl}
                                    alt={`Thumbnail ${index + 1}`}
                                    title={`subTitle ${index + 1}`}
                                    cardMedia={true}
                                    onClick={() => handleImageClick(image, index)}
                                    style={{
                                        // width: "100%",
                                        height: "80px",
                                        objectFit: "cover",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                        minWidth: "135px",
                                        border: index === selectedIndex ? "2px solid green" : "none",
                                        borderRadius: "4px",
                                    }}
                                />
                            ),
                    )}
                </div>

                {showArrows && (
                    <>
                        <div
                            onMouseEnter={() => startScrolling("left")}
                            onMouseLeave={stopScrolling}
                            style={{
                                position: "absolute",
                                left: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "rgba(0, 0, 0, 0.7)",
                                color: "#fff",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "50%",
                                cursor: "pointer",
                                zIndex: 1,
                            }}
                        >
                            &#8592;
                        </div>

                        <div
                            onMouseEnter={() => startScrolling("right")}
                            onMouseLeave={stopScrolling}
                            style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "rgba(0, 0, 0, 0.7)",
                                color: "#fff",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "50%",
                                cursor: "pointer",
                                zIndex: 1,
                            }}
                        >
                            &#8594;
                        </div>
                    </>
                )}
            </div>

            <Lightbox className={className} isOpen={lightboxOpen} imageUrl={lightboxImg} imageCaption={lightboxCaption} onClose={setLightboxOpen} />
        </>
    );
};
